import { useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { assets } from '../data/siteData';
import { assetUrl } from '../lib/media';

export default function Lightbox({ open, items = [], startIndex = 0, onClose, onIndexChange }) {
  const safeItems = useMemo(() => items.filter(Boolean), [items]);
  const currentItem = safeItems[startIndex] || safeItems[0];

  useEffect(() => {
    function handleKeyDown(event) {
      if (!open) return;
      if (event.key === 'Escape') onClose?.();
      if (event.key === 'ArrowLeft' && safeItems.length > 1) {
        onIndexChange?.((startIndex - 1 + safeItems.length) % safeItems.length);
      }
      if (event.key === 'ArrowRight' && safeItems.length > 1) {
        onIndexChange?.((startIndex + 1) % safeItems.length);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, onIndexChange, safeItems.length, startIndex]);

  if (!open || !currentItem) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-brand-black/90 p-4" role="dialog" aria-modal="true" aria-label="Billedvisning">
      <div className="flex min-h-full items-center justify-center">
      <button type="button" className="absolute inset-0 cursor-default" onClick={onClose} aria-label="Luk visning" />

      <div className="relative z-10 my-auto w-full max-w-4xl border border-white/10 bg-brand-black shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 sm:px-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-gray-300">
              {currentItem.label}
            </p>
            {currentItem.caption ? <p className="mt-1 text-sm text-brand-gray-400">{currentItem.caption}</p> : null}
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-10 w-10 items-center justify-center border border-white/10 text-white transition-colors hover:border-brand-orange hover:text-brand-orange" aria-label="Luk visning">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid gap-4 p-4 sm:p-5">
          <div className="relative aspect-[4/3] overflow-hidden bg-brand-gray-900">
            <img
              src={
                currentItem.label === 'Vinduer og Døre'
                  ? assets.windows
                  : assetUrl(currentItem.src, 1200)
              }
              alt={currentItem.alt || currentItem.label}
              className="h-full w-full object-contain"
            />
          </div>

          {safeItems.length > 1 ? (
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => onIndexChange?.((startIndex - 1 + safeItems.length) % safeItems.length)}
                className="inline-flex items-center gap-2 border border-white/10 px-4 py-3 text-sm font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:border-brand-orange hover:text-brand-orange"
              >
                <ChevronLeft className="h-4 w-4" />
                Forrige
              </button>
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-gray-400">
                {startIndex + 1} / {safeItems.length}
              </span>
              <button
                type="button"
                onClick={() => onIndexChange?.((startIndex + 1) % safeItems.length)}
                className="inline-flex items-center gap-2 border border-white/10 px-4 py-3 text-sm font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:border-brand-orange hover:text-brand-orange"
              >
                Næste
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
    </div>
  );
}
